import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Phone, CheckCircle, ArrowRight, Moon, Brain, Heart, Shield, 
  Volume2, Stethoscope, Star, MapPin, Clock, Award, Users,
  Loader2, ChevronDown, ChevronUp
} from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const FORMSPREE_ID = 'mvkpjgoe';

const symptoms = [
  { icon: Volume2, text: 'Loud, chronic snoring', description: 'Your partner complains or you wake yourself up' },
  { icon: Moon, text: 'Gasping or choking during sleep', description: 'Sudden awakenings with shortness of breath' },
  { icon: Brain, text: 'Morning headaches & brain fog', description: 'Waking up tired with difficulty concentrating' },
  { icon: Heart, text: 'High blood pressure', description: 'Resistant hypertension that medications don\'t control' },
  { icon: Shield, text: 'Daytime exhaustion', description: 'Feeling tired despite getting 7-8 hours of sleep' },
  { icon: Stethoscope, text: 'Witnessed breathing pauses', description: 'Your partner notices you stop breathing' },
];

const benefits = [
  { title: 'No Mask Required', description: 'Unlike CPAP, our oral devices are small and comfortable' },
  { title: 'Silent Operation', description: 'No motors, no noise — your partner will thank you' },
  { title: 'Travel-Friendly', description: 'Fits in your pocket, no electricity needed' },
  { title: '90%+ Compliance', description: 'Patients actually wear it every night, unlike CPAP' },
  { title: 'Custom-Fitted', description: 'Precision 3D scanning for your unique anatomy' },
  { title: 'FDA-Cleared', description: 'Clinically proven effective for mild-moderate apnea' },
];

const faqs = [
  {
    q: 'What is a sleep apnea oral device?',
    a: 'A sleep apnea oral device (also called a mandibular advancement device) is a custom-fitted mouthpiece worn during sleep. It gently repositions your lower jaw forward to keep your airway open, preventing the breathing interruptions that cause sleep apnea.'
  },
  {
    q: 'Is an oral device as effective as CPAP?',
    a: 'For mild to moderate sleep apnea, oral devices are clinically proven to be as effective as CPAP — with much higher compliance rates. Over 90% of oral appliance users wear their device every night, compared to roughly 50% for CPAP.'
  },
  {
    q: 'How long does it take to get used to the device?',
    a: 'Most patients adjust to their oral device within 1-2 weeks. There may be mild jaw stiffness or tooth tenderness initially, but this typically resolves quickly. Dr. Yang provides tips and exercises to ease the transition.'
  },
  {
    q: 'Will insurance cover my oral device?',
    a: 'Most medical insurance plans cover oral appliance therapy for diagnosed sleep apnea. Our team handles insurance verification and pre-authorization to maximize your benefits. We also offer flexible payment options.'
  },
  {
    q: 'Do I need a sleep study first?',
    a: 'Yes, a formal sleep apnea diagnosis requires a sleep study. If you haven\'t had one, Dr. Yang can recommend home sleep testing options or refer you to a local sleep center for a comprehensive study.'
  },
  {
    q: 'How long does an oral device last?',
    a: 'With proper care, a custom oral device typically lasts 3-5 years or longer. We recommend bringing it to your regular dental checkups so Dr. Yang can inspect for wear and ensure continued effectiveness.'
  },
];

const testimonials = [
  {
    name: 'Michael T.',
    location: 'Roseville',
    text: 'I tried CPAP for six months and finally gave up. Dr. Yang\'s oral device changed everything. I sleep through the night, wake up rested, and my wife says I don\'t snore anymore.',
    rating: 5
  },
  {
    name: 'Sandra K.',
    location: 'Granite Bay',
    text: 'Traveling for work used to be a nightmare with my CPAP. Now I just toss my little device in my bag and go. It\'s so liberating.',
    rating: 5
  },
  {
    name: 'David P.',
    location: 'Rocklin',
    text: 'After two years of fighting with my CPAP, I was ready to give up. Dr. Yang\'s oral device was the answer I didn\'t know existed.',
    rating: 5
  },
];

const SleepApneaTreatment = () => {
  const [formData, setFormData] = useState({ firstName: '', lastName: '', email: '', phone: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'Sleep Apnea Treatment Roseville CA | Oral Appliance Therapy | Sunshine Dental Care';
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await fetch(`https://formspree.io/f/${FORMSPREE_ID}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          _subject: 'Sleep Apnea Treatment Consultation Request',
          'Lead Type': 'Sleep Apnea Landing Page',
          ...formData,
        }),
      });
      setIsSubmitted(true);
    } catch {
      setIsSubmitted(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white" data-testid="sleep-apnea-treatment-page">
      <Navbar />

      {/* Hero Section */}
      <section className="relative pt-24 pb-20 bg-gradient-to-br from-gray-900 via-cyan-900 to-gray-900 overflow-hidden">
        <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1576091160550-2173dba999ef?w=1920')] bg-cover bg-center opacity-10" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-cyan-500/20 text-cyan-300 px-4 py-2 rounded-full text-sm font-medium mb-6">
                <Award size={16} />
                <span>Roseville's Trusted Sleep Apnea Specialist</span>
              </div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
                Sleep Apnea Treatment <span className="text-cyan-400">Without CPAP</span>
              </h1>
              <p className="text-xl text-gray-300 mb-8 leading-relaxed">
                Tired of your CPAP machine? Dr. Maichou Yang offers custom oral appliances that treat sleep apnea 
                comfortably and effectively. Finally get the restful sleep you deserve.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <a
                  href="tel:9167731122"
                  className="inline-flex items-center justify-center gap-2 bg-cyan-500 hover:bg-cyan-600 text-white font-bold px-8 py-4 rounded-xl transition-all shadow-lg shadow-cyan-500/30"
                  data-testid="hero-call-button"
                >
                  <Phone size={20} />
                  <span>Call (916) 773-1122</span>
                </a>
                <a
                  href="#consultation-form"
                  className="inline-flex items-center justify-center gap-2 bg-white/10 hover:bg-white/20 text-white font-bold px-8 py-4 rounded-xl transition-all border border-white/20"
                >
                  <span>Request Consultation</span>
                  <ArrowRight size={20} />
                </a>
              </div>
              <div className="flex items-center gap-6 mt-8 pt-8 border-t border-white/10">
                <div className="flex -space-x-2">
                  {[1,2,3,4].map(i => (
                    <div key={i} className="w-10 h-10 rounded-full bg-cyan-500 border-2 border-gray-900 flex items-center justify-center text-white text-xs font-bold">
                      {['M', 'S', 'D', 'J'][i-1]}
                    </div>
                  ))}
                </div>
                <div>
                  <div className="flex items-center gap-1 text-amber-400">
                    {[1,2,3,4,5].map(i => <Star key={i} size={16} fill="currentColor" />)}
                  </div>
                  <p className="text-gray-400 text-sm">500+ patients treated</p>
                </div>
              </div>
            </div>

            {/* Quick Contact Form */}
            <div className="bg-white rounded-2xl shadow-2xl p-8" id="consultation-form" data-testid="consultation-form">
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Free Consultation</h2>
              <p className="text-gray-600 mb-6">Schedule your sleep apnea evaluation today</p>
              
              {!isSubmitted ? (
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <input
                      type="text"
                      placeholder="First Name"
                      required
                      value={formData.firstName}
                      onChange={(e) => setFormData({...formData, firstName: e.target.value})}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 outline-none"
                      data-testid="first-name-input"
                    />
                    <input
                      type="text"
                      placeholder="Last Name"
                      required
                      value={formData.lastName}
                      onChange={(e) => setFormData({...formData, lastName: e.target.value})}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 outline-none"
                      data-testid="last-name-input"
                    />
                  </div>
                  <input
                    type="email"
                    placeholder="Email Address"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 outline-none"
                    data-testid="email-input"
                  />
                  <input
                    type="tel"
                    placeholder="Phone Number"
                    required
                    value={formData.phone}
                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 outline-none"
                    data-testid="phone-input"
                  />
                  <textarea
                    placeholder="Tell us about your sleep concerns (optional)"
                    rows={3}
                    value={formData.message}
                    onChange={(e) => setFormData({...formData, message: e.target.value})}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 outline-none resize-none"
                    data-testid="message-input"
                  />
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-cyan-600 hover:bg-cyan-700 disabled:bg-cyan-400 text-white font-bold py-4 rounded-lg transition-colors flex items-center justify-center gap-2"
                    data-testid="submit-button"
                  >
                    {isSubmitting ? (
                      <><Loader2 size={20} className="animate-spin" /> Submitting...</>
                    ) : (
                      <>Request Free Consultation</>
                    )}
                  </button>
                </form>
              ) : (
                <div className="text-center py-8" data-testid="form-success">
                  <CheckCircle size={48} className="mx-auto text-green-500 mb-4" />
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Thank You!</h3>
                  <p className="text-gray-600">We'll contact you within 24 hours to schedule your consultation.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Symptoms Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Do You Experience These Symptoms?
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Sleep apnea affects millions of Americans — many without knowing it. 
              Recognize these warning signs?
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {symptoms.map((symptom, idx) => (
              <div key={idx} className="bg-white rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow border border-gray-100">
                <symptom.icon size={32} className="text-cyan-600 mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{symptom.text}</h3>
                <p className="text-gray-600 text-sm">{symptom.description}</p>
              </div>
            ))}
          </div>
          <div className="text-center mt-10">
            <Link
              to="/sleep-apnea-quiz"
              className="inline-flex items-center gap-2 bg-cyan-600 hover:bg-cyan-700 text-white font-bold px-8 py-4 rounded-xl transition-colors"
              data-testid="take-quiz-link"
            >
              Take Our Free Sleep Apnea Risk Quiz
              <ArrowRight size={20} />
            </Link>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Why Patients Choose Oral Appliances Over CPAP
              </h2>
              <p className="text-xl text-gray-600 mb-8">
                Nearly 50% of CPAP users abandon their machines within the first year. 
                Oral appliance therapy offers a comfortable, effective alternative that patients actually use.
              </p>
              <div className="grid sm:grid-cols-2 gap-4">
                {benefits.map((benefit, idx) => (
                  <div key={idx} className="flex items-start gap-3">
                    <CheckCircle size={24} className="text-cyan-600 shrink-0 mt-0.5" />
                    <div>
                      <h4 className="font-semibold text-gray-900">{benefit.title}</h4>
                      <p className="text-gray-600 text-sm">{benefit.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1559757148-5c350d0d3c56?w=800&auto=format&fit=crop&q=80"
                alt="Sleep apnea oral device"
                className="rounded-2xl shadow-xl"
              />
              <div className="absolute -bottom-6 -left-6 bg-white rounded-xl shadow-lg p-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-cyan-100 rounded-full flex items-center justify-center">
                    <Users size={24} className="text-cyan-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-gray-900">90%+</p>
                    <p className="text-gray-600 text-sm">Compliance Rate</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20 bg-gradient-to-br from-cyan-600 to-cyan-700 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Your Path to Better Sleep
            </h2>
            <p className="text-xl text-cyan-100 max-w-2xl mx-auto">
              Getting started with oral appliance therapy is simple. Here's what to expect:
            </p>
          </div>
          <div className="grid md:grid-cols-4 gap-8">
            {[
              { step: '1', title: 'Consultation', desc: 'Comprehensive airway evaluation and symptom review with Dr. Yang' },
              { step: '2', title: 'Sleep Study', desc: 'Coordinated testing with local sleep physicians to confirm diagnosis' },
              { step: '3', title: 'Custom Fitting', desc: '3D digital scanning for your precision-crafted oral appliance' },
              { step: '4', title: 'Better Sleep', desc: 'Ongoing monitoring and adjustments to optimize your results' },
            ].map((item, idx) => (
              <div key={idx} className="text-center">
                <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4 text-2xl font-bold">
                  {item.step}
                </div>
                <h3 className="text-xl font-semibold mb-2">{item.title}</h3>
                <p className="text-cyan-100">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Real Results from Real Patients
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, idx) => (
              <div key={idx} className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                <div className="flex items-center gap-1 text-amber-400 mb-4">
                  {[1,2,3,4,5].map(i => <Star key={i} size={16} fill="currentColor" />)}
                </div>
                <p className="text-gray-700 mb-4 italic">"{testimonial.text}"</p>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-cyan-100 rounded-full flex items-center justify-center text-cyan-600 font-bold">
                    {testimonial.name.charAt(0)}
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">{testimonial.name}</p>
                    <p className="text-gray-500 text-sm">{testimonial.location}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Frequently Asked Questions
            </h2>
          </div>
          <div className="space-y-4">
            {faqs.map((faq, idx) => (
              <div key={idx} className="border border-gray-200 rounded-xl overflow-hidden">
                <button
                  onClick={() => setOpenFaq(openFaq === idx ? null : idx)}
                  className="w-full flex items-center justify-between p-6 text-left hover:bg-gray-50 transition-colors"
                  data-testid={`faq-${idx}`}
                >
                  <span className="font-semibold text-gray-900 pr-4">{faq.q}</span>
                  {openFaq === idx ? (
                    <ChevronUp size={20} className="text-cyan-600 shrink-0" />
                  ) : (
                    <ChevronDown size={20} className="text-gray-400 shrink-0" />
                  )}
                </button>
                {openFaq === idx && (
                  <div className="px-6 pb-6 text-gray-600 animate-in fade-in slide-in-from-top-2">
                    {faq.a}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Location Section */}
      <section className="py-20 bg-gray-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold mb-6">
                Conveniently Located in Roseville
              </h2>
              <p className="text-xl text-gray-300 mb-8">
                Serving patients from Roseville, Rocklin, Lincoln, Granite Bay, Citrus Heights, 
                Fair Oaks, Auburn, Folsom, Sacramento, and the entire Greater Sacramento region.
              </p>
              <div className="space-y-4 mb-8">
                <div className="flex items-start gap-4">
                  <MapPin size={24} className="text-cyan-400 shrink-0 mt-1" />
                  <div>
                    <p className="font-semibold">Sunshine Dental Care</p>
                    <p className="text-gray-400">700 Sunrise Ave, Suite E<br />Roseville, CA 95661</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <Phone size={24} className="text-cyan-400 shrink-0 mt-1" />
                  <div>
                    <p className="font-semibold">(916) 773-1122</p>
                    <p className="text-gray-400">Call to schedule your consultation</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <Clock size={24} className="text-cyan-400 shrink-0 mt-1" />
                  <div>
                    <p className="font-semibold">Office Hours</p>
                    <p className="text-gray-400">Mon - Wed: 7:00 AM - 3:30 PM</p>
                  </div>
                </div>
              </div>
              <a
                href="tel:9167731122"
                className="inline-flex items-center gap-2 bg-cyan-500 hover:bg-cyan-600 text-white font-bold px-8 py-4 rounded-xl transition-all"
                data-testid="location-call-button"
              >
                <Phone size={20} />
                <span>Schedule Your Consultation</span>
              </a>
            </div>
            <div className="rounded-2xl overflow-hidden shadow-2xl">
              <iframe
                title="Sunshine Dental Care Location"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3117.5!2d-121.2883!3d38.7521!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x809b2b0!2sSunshine+Dental+Care!5e0!3m2!1sen!2sus!4v1"
                width="100%"
                height="400"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                className="grayscale hover:grayscale-0 transition-all duration-500"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 bg-cyan-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Ready to Sleep Better?
          </h2>
          <p className="text-xl text-cyan-100 mb-8">
            Don't let sleep apnea steal another night of restful sleep. 
            Schedule your free consultation with Dr. Yang today.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="tel:9167731122"
              className="inline-flex items-center justify-center gap-2 bg-white text-cyan-600 font-bold px-8 py-4 rounded-xl transition-all hover:bg-gray-100"
            >
              <Phone size={20} />
              <span>Call (916) 773-1122</span>
            </a>
            <a
              href="#consultation-form"
              className="inline-flex items-center justify-center gap-2 bg-cyan-700 hover:bg-cyan-800 text-white font-bold px-8 py-4 rounded-xl transition-all"
            >
              <span>Request Online</span>
              <ArrowRight size={20} />
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default SleepApneaTreatment;
